import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Baby,
  Plus,
  Pencil,
  Trash2,
  ArrowRightLeft,
  X,
} from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select, type SelectOption } from "@/components/ui/Select";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/Card";
import { cn } from "@/lib/cn";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CribAssignment {
  id: number;
  bedId: number;
  bedName: string;
  roomName: string | null;
  assignedDate: string;
  notes: string | null;
}

interface CribRecord {
  id: number;
  cribName: string;
  isActive: boolean;
  notes: string | null;
  createdAt: string;
  assignment: CribAssignment | null;
}

interface CribSummary {
  total: number;
  assigned: number;
  available: number;
  inactive: number;
}

interface BedEntry {
  bed: {
    id: number;
    roomName: string | null;
    bedName: string;
    isActive: boolean;
  };
  status: string;
}

interface BedBoardResponse {
  data: {
    beds: BedEntry[];
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CribManagement() {
  const queryClient = useQueryClient();
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCrib, setEditingCrib] = useState<CribRecord | null>(null);
  const [assigningCrib, setAssigningCrib] = useState<CribRecord | null>(null);

  // Fetch cribs
  const { data: cribsData, isLoading } = useQuery<{ data: CribRecord[] }>({
    queryKey: ["cribs"],
    queryFn: () => api.get<{ data: CribRecord[] }>("/shelter/cribs"),
  });

  // Fetch summary
  const { data: summaryData } = useQuery<{ data: CribSummary }>({
    queryKey: ["cribs-summary"],
    queryFn: () => api.get<{ data: CribSummary }>("/shelter/cribs/summary"),
  });

  // Fetch beds for assignment dropdown
  const { data: boardData } = useQuery<BedBoardResponse>({
    queryKey: ["bed-board"],
    queryFn: () => api.get<BedBoardResponse>("/shelter/board"),
  });

  const invalidateCribs = () => {
    queryClient.invalidateQueries({ queryKey: ["cribs"] });
    queryClient.invalidateQueries({ queryKey: ["cribs-summary"] });
  };

  // Mutations
  const createMutation = useMutation({
    mutationFn: (body: { cribName: string; notes?: string }) =>
      api.post("/shelter/cribs", body),
    onSuccess: () => {
      invalidateCribs();
      setShowAddForm(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: number;
      cribName?: string;
      notes?: string;
      isActive?: boolean;
    }) => api.put(`/shelter/cribs/${id}`, body),
    onSuccess: () => {
      invalidateCribs();
      setEditingCrib(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/shelter/cribs/${id}`),
    onSuccess: invalidateCribs,
  });

  const assignMutation = useMutation({
    mutationFn: ({
      cribId,
      bedId,
      notes,
    }: {
      cribId: number;
      bedId: string;
      notes?: string;
    }) =>
      api.post(`/shelter/cribs/${cribId}/assign`, {
        bedId,
        date: new Date().toISOString().slice(0, 10),
        notes,
      }),
    onSuccess: () => {
      invalidateCribs();
      queryClient.invalidateQueries({ queryKey: ["bed-board"] });
      setAssigningCrib(null);
    },
  });

  const unassignMutation = useMutation({
    mutationFn: (cribId: number) =>
      api.post(`/shelter/cribs/${cribId}/unassign`, {
        date: new Date().toISOString().slice(0, 10),
      }),
    onSuccess: () => {
      invalidateCribs();
      queryClient.invalidateQueries({ queryKey: ["bed-board"] });
    },
  });

  const cribs = cribsData?.data ?? [];
  const summary = summaryData?.data;

  // Build bed options for assignment (only occupied beds — cribs go to rooms with people)
  const allBeds = boardData?.data?.beds ?? [];
  const bedOptions: SelectOption[] = allBeds
    .filter((b) => b.bed.isActive)
    .map((b) => ({
      value: String(b.bed.id),
      label: b.bed.roomName
        ? `${b.bed.roomName} - ${b.bed.bedName}`
        : b.bed.bedName,
    }));

  // Separate cribs by status
  const assignedCribs = cribs.filter((c) => c.isActive && c.assignment);
  const availableCribs = cribs.filter((c) => c.isActive && !c.assignment);
  const inactiveCribs = cribs.filter((c) => !c.isActive);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Baby className="h-5 w-5" />
            Cribs
          </h3>
          <p className="text-sm text-muted-foreground">
            Manage crib inventory and assign to rooms/beds.
          </p>
        </div>
        <Button
          size="sm"
          className="gap-2"
          onClick={() => {
            setShowAddForm(true);
            setEditingCrib(null);
          }}
        >
          <Plus className="h-4 w-4" />
          Add Crib
        </Button>
      </div>

      {/* Summary stats */}
      {summary && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatCard label="Total Cribs" value={summary.total} color="text-foreground" />
          <StatCard label="Assigned" value={summary.assigned} color="text-blue-600" />
          <StatCard label="Available" value={summary.available} color="text-green-600" />
          <StatCard label="Inactive" value={summary.inactive} color="text-gray-500" />
        </div>
      )}

      {/* Add form */}
      {showAddForm && (
        <Card className="border-primary/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Add New Crib</CardTitle>
          </CardHeader>
          <CardContent>
            <CribForm
              onSubmit={(d) => createMutation.mutate(d)}
              onCancel={() => setShowAddForm(false)}
              isLoading={createMutation.isPending}
              error={
                createMutation.isError
                  ? ((createMutation.error as any)?.body?.error ??
                      (createMutation.error as any)?.body?.message ??
                      "Failed to create crib")
                  : undefined
              }
            />
          </CardContent>
        </Card>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="h-24 animate-pulse rounded-lg border bg-muted"
            />
          ))}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && cribs.length === 0 && !showAddForm && (
        <Card>
          <CardContent className="p-8 text-center">
            <Baby className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">
              No cribs added yet. Click "Add Crib" to get started.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Available cribs */}
      {availableCribs.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-semibold text-green-700 uppercase tracking-wide">
            Available ({availableCribs.length})
          </h4>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
            {availableCribs.map((crib) =>
              editingCrib?.id === crib.id ? (
                <Card key={crib.id} className="col-span-2 border-primary/30">
                  <CardContent className="p-3">
                    <CribForm
                      initial={crib}
                      onSubmit={(d) =>
                        updateMutation.mutate({ id: crib.id, ...d })
                      }
                      onCancel={() => setEditingCrib(null)}
                      isLoading={updateMutation.isPending}
                    />
                  </CardContent>
                </Card>
              ) : (
                <div
                  key={crib.id}
                  className="rounded-lg border-2 bg-green-50 border-green-200 p-3"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-semibold">{crib.cribName}</p>
                      <p className="text-xs text-green-700 mt-0.5">Available</p>
                    </div>
                    <div className="h-2.5 w-2.5 rounded-full bg-green-500 shrink-0" />
                  </div>
                  {crib.notes && (
                    <p className="text-xs text-muted-foreground mt-1 truncate">
                      {crib.notes}
                    </p>
                  )}
                  <div className="flex gap-1 mt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs gap-1 flex-1"
                      onClick={() => setAssigningCrib(crib)}
                    >
                      <ArrowRightLeft className="h-3 w-3" />
                      Assign
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => {
                        setEditingCrib(crib);
                        setShowAddForm(false);
                      }}
                    >
                      <Pencil className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => {
                        if (confirm("Deactivate this crib?"))
                          deleteMutation.mutate(crib.id);
                      }}
                    >
                      <Trash2 className="h-3 w-3 text-destructive" />
                    </Button>
                  </div>
                </div>
              ),
            )}
          </div>
        </div>
      )}

      {/* Assigned cribs */}
      {assignedCribs.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-semibold text-blue-700 uppercase tracking-wide">
            Assigned ({assignedCribs.length})
          </h4>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
            {assignedCribs.map((crib) =>
              editingCrib?.id === crib.id ? (
                <Card key={crib.id} className="col-span-2 border-primary/30">
                  <CardContent className="p-3">
                    <CribForm
                      initial={crib}
                      onSubmit={(d) =>
                        updateMutation.mutate({ id: crib.id, ...d })
                      }
                      onCancel={() => setEditingCrib(null)}
                      isLoading={updateMutation.isPending}
                    />
                  </CardContent>
                </Card>
              ) : (
                <div
                  key={crib.id}
                  className="rounded-lg border-2 bg-blue-50 border-blue-200 p-3"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-semibold">{crib.cribName}</p>
                      <p className="text-xs text-blue-700 mt-0.5 truncate">
                        {crib.assignment?.roomName
                          ? `${crib.assignment.roomName} - `
                          : ""}
                        {crib.assignment?.bedName}
                      </p>
                    </div>
                    <div className="h-2.5 w-2.5 rounded-full bg-blue-500 shrink-0" />
                  </div>
                  {crib.assignment?.assignedDate && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Since{" "}
                      {new Date(
                        crib.assignment.assignedDate,
                      ).toLocaleDateString()}
                    </p>
                  )}
                  <div className="flex gap-1 mt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs gap-1 flex-1"
                      onClick={() => unassignMutation.mutate(crib.id)}
                      loading={unassignMutation.isPending}
                    >
                      <X className="h-3 w-3" />
                      Remove
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => {
                        setEditingCrib(crib);
                        setShowAddForm(false);
                      }}
                    >
                      <Pencil className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ),
            )}
          </div>
        </div>
      )}

      {/* Inactive cribs */}
      {inactiveCribs.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
            Inactive ({inactiveCribs.length})
          </h4>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
            {inactiveCribs.map((crib) => (
              <div
                key={crib.id}
                className="rounded-lg border-2 bg-gray-50 border-gray-200 p-3 opacity-60"
              >
                <div className="flex items-start justify-between">
                  <p className="text-sm font-semibold">{crib.cribName}</p>
                  <div className="h-2.5 w-2.5 rounded-full bg-gray-400 shrink-0" />
                </div>
                <p className="text-xs text-gray-500 mt-0.5">Inactive</p>
                <div className="mt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() =>
                      updateMutation.mutate({ id: crib.id, isActive: true })
                    }
                  >
                    Reactivate
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Assign dialog */}
      {assigningCrib && (
        <AssignCribDialog
          crib={assigningCrib}
          bedOptions={bedOptions}
          onAssign={(bedId, notes) =>
            assignMutation.mutate({
              cribId: assigningCrib.id,
              bedId,
              notes,
            })
          }
          onClose={() => setAssigningCrib(null)}
          isLoading={assignMutation.isPending}
          error={
            assignMutation.isError
              ? ((assignMutation.error as any)?.body?.error ??
                  (assignMutation.error as any)?.body?.message ??
                  "Failed to assign crib")
              : undefined
          }
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stat Card
// ---------------------------------------------------------------------------

function StatCard({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color: string;
}) {
  return (
    <Card>
      <CardContent className="flex items-center gap-3 p-4">
        <Baby className={cn("h-8 w-8", color)} />
        <div>
          <p className="text-2xl font-bold">{value}</p>
          <p className="text-xs text-muted-foreground">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Crib Form
// ---------------------------------------------------------------------------

function CribForm({
  initial,
  onSubmit,
  onCancel,
  isLoading,
  error,
}: {
  initial?: CribRecord;
  onSubmit: (data: { cribName: string; notes?: string }) => void;
  onCancel: () => void;
  isLoading: boolean;
  error?: string;
}) {
  const [cribName, setCribName] = useState(initial?.cribName ?? "");
  const [notes, setNotes] = useState(initial?.notes ?? "");

  return (
    <div className="space-y-3">
      <Input
        label="Crib Name"
        value={cribName}
        onChange={(e) => setCribName(e.target.value)}
        placeholder="e.g., Crib 1, Pack-n-Play A..."
      />
      <Input
        label="Notes"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Optional notes..."
      />
      {error && (
        <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}
      <div className="flex gap-2">
        <Button
          size="sm"
          onClick={() => onSubmit({ cribName, notes: notes || undefined })}
          loading={isLoading}
          disabled={!cribName.trim()}
        >
          {initial ? "Update" : "Add Crib"}
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Assign Crib Dialog
// ---------------------------------------------------------------------------

function AssignCribDialog({
  crib,
  bedOptions,
  onAssign,
  onClose,
  isLoading,
  error,
}: {
  crib: CribRecord;
  bedOptions: SelectOption[];
  onAssign: (bedId: string, notes?: string) => void;
  onClose: () => void;
  isLoading: boolean;
  error?: string;
}) {
  const [selectedBedId, setSelectedBedId] = useState("");
  const [notes, setNotes] = useState("");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="mx-4 w-full max-w-md rounded-lg border bg-background shadow-lg">
        <div className="flex items-center justify-between border-b p-4">
          <h3 className="text-lg font-semibold">
            Assign {crib.cribName}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 hover:bg-accent"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="space-y-4 p-4">
          <Select
            label="Assign to Room / Bed"
            options={bedOptions}
            value={selectedBedId}
            onChange={setSelectedBedId}
            placeholder="Select a bed..."
            searchable
          />
          <Input
            label="Notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g., Requested by client"
          />
          {error && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 border-t p-4">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => onAssign(selectedBedId, notes || undefined)}
            loading={isLoading}
            disabled={!selectedBedId}
          >
            Assign Crib
          </Button>
        </div>
      </div>
    </div>
  );
}
